/* eslint-disable import/no-restricted-paths */
import { useState } from "react";

import type { ChillnnGraphQLError, GraphQLErrors } from "@/lib/apollo-client/custom-error";
import type { ContextSetter, ErrorHandler, HttpOptions } from "@/lib/apollo-client/custom-provider";
import { ApolloProvider, clientInitializer } from "@/lib/apollo-client/custom-provider";
import { AuthWrapper } from "@/lib/aws-amplify";

import { DevelopmentTools } from "@/hooks/use-development-tools";

import { useNewRelic } from "../new-relic";

import type { Operation } from "@apollo/client";
import type { NetworkError } from "@apollo/client/errors";
import type { BrowserAgent } from "@newrelic/browser-agent/src/loaders/browser-agent.js";
import type { FormattedExecutionResult } from "graphql";
import type { GraphQLFormattedError } from "graphql/error/formatError";

const { consoleErrorOnlyDevMode } = DevelopmentTools();

/**
 * Chillnn エラーの判定
 */
const isChillnnGraphQLError = (error: GraphQLErrors[number]): error is ChillnnGraphQLError => {
	return (
		error &&
		!!error.extensions &&
		"chillnnErrorMessageForDeveloper" in error.extensions &&
		"chillnnErrorMessageForUser" in error.extensions &&
		"chillnnStatusCode" in error.extensions
	);
};

// 共通のエラー通知関数
const noticeErrorToNewRelic = ({
	newRelic,
	errorType,
	error,
	operation,
	response,
}: {
	newRelic: BrowserAgent;
	errorType: "GraphQL CHILLNN Error" | "GraphQL Error" | "Network Error" | "Protocol Error";
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	error: ChillnnGraphQLError | GraphQLFormattedError<Record<string, any>> | NetworkError;
	operation: Operation;
	response:
		| FormattedExecutionResult<
				{
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					[key: string]: any;
				},
				{
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					[key: string]: any;
				}
		  >
		| undefined;
}) => {
	newRelic.noticeError(
		new Error(
			error && isChillnnGraphQLError(error)
				? `[${errorType}]${error?.message}\noperationName: ${operation.operationName}\nchillnnStatusCode: ${error.extensions.chillnnStatusCode}\nchillnnErrorMessageForDeveloper: ${error.extensions?.chillnnErrorMessageForDeveloper || "なし"}\nchillnnErrorMessageForUser: ${error.extensions.chillnnErrorMessageForUser.ja}\n\nvariables: ${JSON.stringify(operation.variables)}`
				: `[${errorType}]${error?.message}\noperationName: ${operation.operationName}\n\nvariables: ${JSON.stringify(operation.variables)}\n`,
			{ cause: error },
		),
		{
			attributes: {
				errorType,
				...error,
				...response,
				...operation,
			},
		},
	);
};

// エラーハンドラー
const errorHandler: (props: { newRelic: BrowserAgent }) => ErrorHandler =
	({ newRelic }) =>
	({ graphQLErrors, networkError, protocolErrors, response, forward, operation }) => {
		if (graphQLErrors) {
			graphQLErrors.forEach((error, i) => {
				if (isChillnnGraphQLError(error)) {
					consoleErrorOnlyDevMode(`[${i}] GraphQL CHILLNN Error`, error);

					noticeErrorToNewRelic({
						newRelic,
						errorType: "GraphQL CHILLNN Error",
						error,
						operation,
						response,
					});
				} else {
					consoleErrorOnlyDevMode("GraphQL Error", error);

					noticeErrorToNewRelic({
						newRelic,
						errorType: "GraphQL Error",
						error,
						operation,
						response,
					});
				}
			});
		}

		if (networkError) {
			/**
			 * スナック編集ページからブラウザバックで戻った際に発生するエラー のハンドリング
			 * エラーネームが "TypeError" エラーメッセージが "Failed to fetch" の場合は、リクエストを再送する
			 * この処理を試験的に採用している。採用するかについては様子を見てから検討したい。
			 */
			if (networkError.name === "TypeError" && networkError.message === "Failed to fetch") {
				return forward(operation);
			}

			consoleErrorOnlyDevMode("Network Error", networkError);

			noticeErrorToNewRelic({
				newRelic,
				errorType: "Network Error",
				error: networkError,
				operation,
				response,
			});
		}

		if (protocolErrors) {
			protocolErrors.forEach((error, i) => {
				consoleErrorOnlyDevMode(`[${i}] Protocol Error`, error);

				noticeErrorToNewRelic({
					newRelic,
					errorType: "Protocol Error",
					error,
					operation,
					response,
				});
			});
		}
	};

// エンドポイントの設定
const httpOptions: HttpOptions = {
	uri: import.meta.env.VITE_GRAPHQL_API_ENDPOINT,
};

// Contextの設定
const contextSetter: ContextSetter = async (_, prevContext) => {
	const token = await AuthWrapper.getIdToken();
	const authorization = token ? `Bearer ${token}` : "";

	return {
		...prevContext,
		headers: {
			...prevContext.headers,
			authorization,
		},
	};
};

export const ApolloClientProvider = ({ children }: { children: React.ReactNode }) => {
	const { newRelic } = useNewRelic();
	const [apolloClient] = useState(clientInitializer(errorHandler({ newRelic }), httpOptions, contextSetter));

	// ApolloProviderのラップ
	return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
